import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Landmark } from "lucide-react";
import { Section, ErrorNote, Pill, goToStripe } from "@/components/connect/shared";

// Step 1: create the Stripe connected account (V2) using the host's display name + email.
function CreateAccountForm({ status }) {
  const qc = useQueryClient();
  const [name, setName] = useState(status.suggested_name || "");
  const [email, setEmail] = useState(status.suggested_email || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/connect/account", { display_name: name, contact_email: email });
      toast.success("Stripe account created. Next, finish onboarding.");
      qc.invalidateQueries({ queryKey: ["connect-account"] });
    } catch (err) {
      setError(err);
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={create} className="grid gap-4 sm:grid-cols-2" data-testid="connect-create-form">
      {error && <div className="sm:col-span-2"><ErrorNote error={error} testid="connect-create-error" /></div>}      <div><Label htmlFor="cn">Business or display name</Label><Input id="cn" data-testid="connect-display-name-input" value={name} onChange={(e) => setName(e.target.value)} required /></div>
      <div><Label htmlFor="ce">Contact email</Label><Input id="ce" type="email" data-testid="connect-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
      <Button type="submit" disabled={busy} className="rounded-full sm:col-span-2 sm:w-fit" data-testid="connect-create-account-button">
        {busy ? "Creating…" : "Create my Stripe account"}
      </Button>
    </form>
  );
}

// Step 2: live onboarding status (read from Stripe every time) + Account Link button.
function StatusView({ status }) {
  const label = status.onboarding_complete ? "Update details" : "Onboard to collect payments";
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium" data-testid="connect-account-name">{status.display_name}</span>
        <span className="text-stone-400" data-testid="connect-account-id">{status.account_id}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Pill ok={status.onboarding_complete} testid="connect-onboarding-status">{status.onboarding_complete ? "Onboarding complete" : `Info needed (${status.requirements_status})`}</Pill>
        <Pill ok={status.ready_to_process_payments} testid="connect-payments-status">{status.ready_to_process_payments ? "Ready to take payments" : `Card payments: ${status.card_payments_status}`}</Pill>
      </div>
      <Button className="rounded-full" data-testid="connect-onboard-button" onClick={() => goToStripe("/connect/onboarding-link")}>{label}</Button>
    </div>
  );
}

export function OnboardingCard({ query }) {
  const { data, error, isLoading } = query;
  return (
    <Section testid="connect-onboarding-card" icon={Landmark} title="Get paid with Stripe" subtitle="Connect a Stripe account so guests can pay you directly. FairStay keeps a flat 10%.">
      {isLoading && <p className="text-sm text-stone-500">Checking your Stripe status…</p>}
      {error && <ErrorNote error={error} testid="connect-status-error" />}
      {data && !data.connected && <CreateAccountForm status={data} />}
      {data?.connected && <StatusView status={data} />}
    </Section>
  );
}
